import { useDispatch } from 'react-redux';

// material-ui
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormLabel,
  Grid,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third-party
import * as Yup from 'yup';
import { Form, FormikProvider, FormikErrors, FormikValues, setNestedObjectValues, useFormik } from 'formik';

// project imports
import IconButton from 'components/@extended/IconButton';
import { openSnackbar } from 'store/reducers/snackbar';

// assets
import { CameraOutlined, DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { Company, CompanyType, useIsCompanyAddedLazyQuery, useSaveCompanyMutation, useSaveCompanyOwnersMutation } from '../../graphql/types';
import { PatternFormat } from 'react-number-format';
import React, { ChangeEvent, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { AppConfig } from '../../config';
import fileToBase64 from '../../utils/fileToBase64';
import ProfileAvatarEditor from '../profiles/account/ProfileAvatarEditor';
import Avatar from '../../components/@extended/Avatar';
import { stringAvatar } from '../../utils/avatar';
import { useTheme } from '@mui/material/styles';
import CompanyTypeAutocomplete from './CompanyTypeAutocomplete';
import CompanyOwnersTab, { CompanyInfoFormValues, CompanyOwnerFormValues, CompanyOwnersTabHandle } from './CompanyOwnersTab';

// ==============================|| MERCHANT ADD / EDIT / DELETE ||============================== //

export interface Props {
  entity?: any;
  onCancel: () => void;
  onSave: (company: Company) => void;
  companyTypes?: CompanyType[];
  activeSubTab?: number;
  onNavigateToMainTab?: (tabIndex: number) => void;
  onAfterCompanyPersist?: (company: Company) => Promise<void> | void;
}

function getFirstErrorPath(errors: FormikErrors<CompanyInfoFormValues>): string | null {
  const walk = (node: unknown, prefix: string): string | null => {
    if (node == null) return null;
    if (typeof node === 'string') return prefix || null;
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) {
        const el = node[i];
        if (el !== undefined && el !== null) {
          const next = walk(el, prefix ? `${prefix}.${i}` : String(i));
          if (next) return next;
        }
      }
      return null;
    }
    if (typeof node === 'object') {
      for (const key of Object.keys(node as object)) {
        const path = prefix ? `${prefix}.${key}` : key;
        const next = walk((node as Record<string, unknown>)[key], path);
        if (next) return next;
      }
    }
    return null;
  };
  return walk(errors, '');
}
const getInitialValues = (entity: FormikValues | null, companyTypes?: CompanyType[]): CompanyInfoFormValues => {
  const newEntity: CompanyInfoFormValues = {
    legalName: '',
    dba: '',
    email: [] as string[],
    www: '',
    phone: [] as string[],
    taxId: '',
    type: companyTypes || [],
    linkToFiles: '',
    owners: entity?.owners?.length
      ? (entity.owners as FormikValues[]).map((owner): CompanyOwnerFormValues => ({
          id: owner.id || '',
          firstName: owner.firstName || '',
          lastName: owner.lastName || '',
          middleName: owner.middleName || '',
          bizPhone: owner.bizPhone || '',
          cellPhone: owner.cellPhone || '',
          bizEmail: owner.bizEmail || '',
          dateOfBirth: owner.dateOfBirth || '',
          ssn: owner.ssn || '',
          addressLine1: owner.address?.line1 || '',
          city: owner.address?.city || '',
          state: owner.address?.state || '',
          zip: owner.address?.zip || ''
        }))
      : []
  };

  if (entity) {
    newEntity.legalName = entity.legalName || '';
    newEntity.dba = entity.dba || '';
    newEntity.email = Array.isArray(entity.email) ? entity.email : [];
    newEntity.phone = Array.isArray(entity.phone) ? entity.phone : [];
    newEntity.taxId = entity.taxId || '';
    newEntity.type = Array.isArray(entity.type) ? entity.type : entity.type ? [entity.type] : [];
    newEntity.www = entity.www || '';
    newEntity.linkToFiles = entity.linkToFiles || '';
  }

  return newEntity;
};
const CompanyInfoTab = forwardRef(
  (
    { entity, companyTypes, onCancel, onSave, activeSubTab = 0, onNavigateToMainTab, onAfterCompanyPersist }: Props,
    ref,
  ) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const ownersTabRef = useRef<CompanyOwnersTabHandle>(null);
  const [updateCompany] = useSaveCompanyMutation();
  const [saveCompanyOwners] = useSaveCompanyOwnersMutation();
  const [checkCompanyExists, { loading: isChecking }] = useIsCompanyAddedLazyQuery({
    fetchPolicy: 'network-only'
  });
  const CustomerSchema = Yup.object().shape({
    legalName: Yup.string()
      .max(256)
      .required('Legal Entity Name is required')
      .test('unique-company', 'Company already exists', async function (value) {
        if (!value || entity?.id) return true;
        const res = await checkCompanyExists({ variables: { legalName: value } });
        return !res.data?.isCompanyAdded;
      }),
    dba: Yup.string().max(256).required('Legal Entity Name is required'),
    email: Yup.array().of(Yup.string().email('Invalid email address')).nullable(),
    phone: Yup.array().of(
      Yup.string()
        .nullable()
        .test('phone-validation', 'Phone must be 10 digits USA number', function (value) {
          if (!value || value.replace(/\D/g, '').length === 0) return true;
          if (value.replace(/\D/g, '').length !== 11) return this.createError({ message: 'Phone must be 10 digits USA number' });
          return true;
        })
    ).nullable(),
    type: Yup.array().of(Yup.mixed<CompanyType>()).min(1, 'At least one type is required.').required('Type is required.'),
    linkToFiles: Yup.string().nullable().matches(/^https?:\/\/.*$/, 'Must be a valid HTTP(S) link'),
    owners: Yup.array().of(
        Yup.object().shape({
          bizEmail: Yup.string().email("Invalid email address"),
          firstName: Yup.string().required("First Name is required"),
          lastName: Yup.string().required("Last Name is required"),
          addressLine1: Yup.string().required("Address is required"),
          city: Yup.string().required("City is required"),
          state: Yup.string().required("State is required"),
          zip: Yup.string()
            .trim()
            .required("Zip is required")
            .matches(/^\d{5}(-\d{4})?$/, 'ZIP must be 5 or 5-4 digits'),
        }),
      )
  });
  const [logo, setLogo] = useState<any | undefined>(undefined);
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [adjustAvatar, setAdjustAvatar] = useState<boolean>(false);
  const [mediaFile, setMediaFile] = useState<File | null>();
  const [activeEmailIndex, setActiveEmailIndex] = useState(0);
  const [activePhoneIndex, setActivePhoneIndex] = useState(0);

  const addEmailField = () => {
    formik.setFieldValue('email', [...formik.values.email, '']);
    setActiveEmailIndex(formik.values.email.length);
  };

  const updateEmailField = (index: number, value: string) => {
    const updatedEmails = [...formik.values.email];
    updatedEmails[index] = value;
    formik.setFieldValue('email', updatedEmails);
  };

  const deleteEmailField = (index: number) => {
    const newEmails = [...formik.values.email];
    newEmails.splice(index, 1);
    formik.setFieldValue('email', newEmails);
    setActiveEmailIndex(Math.max(0, index - 1));
  };

  const addPhoneField = () => {
    formik.setFieldValue('phone', [...formik.values.phone, '']);
    setActivePhoneIndex(formik.values.phone.length);
  };

  const updatePhoneField = (index: number, value: string) => {
    const updatedPhones = [...formik.values.phone];
    updatedPhones[index] = value;
    formik.setFieldValue('phone', updatedPhones);
  };

  const deletePhoneField = (index: number) => {
    const updated = [...formik.values.phone];
    updated.splice(index, 1);
    formik.setFieldValue('phone', updated);
    setActivePhoneIndex((prev) => Math.max(0, prev - 1));
  };

  const handleCloseAvatarAdjustment = () => {
    setAdjustAvatar(!adjustAvatar);
    (document.getElementById('change-company-avtar') as HTMLInputElement).value = '';
    //setSelectedImage(undefined);
  };
  const handleDeleteAvatar = () => {
    setSelectedImage(undefined);
    setLogo(null);
    setMediaFile(null);
    formik.setFieldValue('logo', null);
  };

  useEffect(() => {
    setLogo(entity?.logo ? AppConfig.apiRoot + 'files/avatar/' + entity?.logo : undefined);
  }, [entity]);

  useEffect(() => {
    const fetchData = async (file: File) => {
      setMediaFile(file);
      const base64 = await fileToBase64(file);
      setLogo('data:image/png;base64,' + base64);
      setAdjustAvatar(true);
    };
    if (selectedImage) {
      // setAvatar(URL.createObjectURL(selectedImage));
      fetchData(selectedImage).catch(console.error);
    }
  }, [selectedImage]);
  const initialValues = useMemo(
    () => getInitialValues(entity, companyTypes),
    [entity?.id] 
  );
  const formik = useFormik<CompanyInfoFormValues>({
    initialValues,
    validationSchema: CustomerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const saved = await updateCompany({
          variables: {
            dto: {
              id: entity ? entity.id : null,
              legalName: values.legalName,
              dba: values.dba,
              email: values.email || [],
              phone: values.phone || [],
              taxId: values.taxId,
              type: values.type,
              www: values.www,
              linkToFiles:values.linkToFiles,
              logoMediaFile: mediaFile == null ? mediaFile : ((await fileToBase64(mediaFile)) as string)
            }
          }
        });

        const companyId = saved.data?.saveCompany?.id;
        if (companyId) {
          await saveCompanyOwners({
            variables: {
              companyId,
              owners: values.owners.map((o) => ({
                id: o.id || undefined, 
                firstName: o.firstName,
                lastName: o.lastName,
                middleName: o.middleName || undefined,
                bizPhone: o.bizPhone || undefined,
                cellPhone: o.cellPhone || undefined,
                bizEmail: o.bizEmail || undefined,
                dateOfBirth: o.dateOfBirth || undefined,
                ssn: o.ssn || undefined,
                address: {
                  line1: o.addressLine1,
                  city: o.city,
                  state: o.state,
                  zip: o.zip
                }
              }))
            }
          });
        }

        const company = saved.data?.saveCompany as Company;
        if (onAfterCompanyPersist && company?.id) {
          try {
            await onAfterCompanyPersist(company);
          } catch (e) {
            console.error(e);
          }
        }
        onSave(company);
        dispatch(
          openSnackbar({
            open: true,
            message: 'Operation performed successfully',
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: true
          })
        );
        setSubmitting(false);
      } catch (error) {
        console.error(error);
      }
    }
  });

  const formikRef = useRef(formik);
  formikRef.current = formik;

  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      const f = formikRef.current;
      const validationErrors = await f.validateForm();
      if (Object.keys(validationErrors).length === 0) {
        await f.submitForm();
        return;
      }
      f.setTouched(setNestedObjectValues(f.values, true));
      if (!onNavigateToMainTab) return;
      const firstPath = getFirstErrorPath(validationErrors);
      if (!firstPath) return;
      if (firstPath === 'owners' || firstPath.startsWith('owners.')) {
        onNavigateToMainTab(1);
        const ownersMatch = firstPath.match(/^owners\.(\d+)/);
        const idx = ownersMatch ? parseInt(ownersMatch[1], 10) : 0;
        setTimeout(() => ownersTabRef.current?.setActiveOwnerIndex(idx), 0);
      } else {
        onNavigateToMainTab(0);
      }
    }
  }));

  const { errors, touched, handleSubmit, handleBlur, values, handleChange, getFieldProps } = formik;

  return (
    <>
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Divider />
            <DialogContent sx={{ p: 0 }}>
              <Box
                sx={{
                  display: activeSubTab === 0 ? "block" : "none",
                  p: 2.5,
                }}
              >
                <Grid container spacing={3}>
                  <Grid
                    container
                    spacing={2}
                    alignItems="flex-start"
                    sx={{ mt: 1 }}
                  >
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        flexDirection="row"
                        gap={4}
                        alignItems="flex-start"
                        width="100%"
                      >
                        {/* Avatar Section */}
                        <Stack
                          spacing={1.5}
                          alignItems="center"
                          sx={{ ml: 3, mt: 0.5, position: "relative" }}
                        >
                          <FormLabel
                            htmlFor="change-company-avtar"
                            sx={{
                              position: "relative",
                              borderRadius: "50%",
                              overflow: "hidden",
                              "&:hover .MuiBox-root": { opacity: 1 },
                              cursor: "pointer",
                            }}
                          >
                            {logo ? (
                              <Avatar
                                alt="Company logo"
                                src={logo}
                                sx={{
                                  width: 150,
                                  height: 150,
                                  border: "1px dashed",
                                }}
                              />
                            ) : (
                              <Avatar
                                alt="Company logo"
                                {...stringAvatar(
                                  (entity?.legalName?.trim() ||
                                    "New Company") + " ",
                                )}
                                sx={{
                                  width: 150,
                                  height: 150,
                                  border: "1px dashed",
                                }}
                              />
                            )}
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, .75)"
                                    : "rgba(0,0,0,.65)",
                                width: "100%",
                                height: "100%",
                                opacity: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Stack spacing={0.5} alignItems="center">
                                <CameraOutlined
                                  style={{
                                    color: theme.palette.secondary.lighter,
                                    fontSize: "2rem",
                                  }}
                                />
                                <Typography
                                  sx={{ color: "secondary.lighter" }}
                                >
                                  Upload
                                </Typography>
                              </Stack>
                            </Box>
                          </FormLabel>

                          <IconButton
                            aria-label="delete avatar"
                            color="error"
                            sx={{
                              position: "absolute",
                              top: "100px",
                              left: "141px",
                            }}
                            onClick={handleDeleteAvatar}
                          >
                            <DeleteFilled />
                          </IconButton>

                          <TextField
                            variant="filled"
                            type="file"
                            id="change-company-avtar"
                            label="Outlined"
                            sx={{ display: "none" }}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              setSelectedImage(e.target.files?.[0])
                            }
                          />
                        </Stack>

                        {/* Form Fields */}
                        <Stack spacing={2} flex={1}>
                          <Stack spacing={1.25}>
                            <InputLabel required={true} htmlFor="entity-name">
                              Legal Entity Name / DBA
                            </InputLabel>
                            <TextField
                              variant="filled"
                              fullWidth
                              id="entity-name"
                              placeholder="Legal Entity Name"
                              {...getFieldProps("legalName")}
                              error={Boolean(
                                touched.legalName && errors.legalName,
                              )}
                              helperText={
                                touched.legalName && errors.legalName
                              }
                            />
                          </Stack>

                          <Stack spacing={1.25}>
                            <TextField
                              variant="filled"
                              fullWidth
                              id="entity-dba"
                              placeholder="DBA"
                              {...getFieldProps("dba")}
                              error={Boolean(touched.dba && errors.dba)}
                              helperText={touched.dba && errors.dba}
                            />
                          </Stack>
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid item xs={6}>
                    <Stack spacing={1.25}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            overflowX: "auto",
                            overflowY: "hidden",
                            whiteSpace: "nowrap",
                            display: "flex",
                            flexGrow: 1,
                            alignItems: "center",
                            "&::-webkit-scrollbar": { height: 6 },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#ccc",
                              borderRadius: "4px",
                            },
                          }}
                        >
                          {(formik.values.email.length > 0
                            ? formik.values.email
                            : [""]
                          ).map((_, index: number) => (
                            <Box
                              key={index}
                              onClick={() => setActiveEmailIndex(index)}
                              sx={{
                                cursor: "pointer",
                                px: 1.5,
                                py: 0.5,
                                fontSize: "0.875rem",
                                borderBottom:
                                  activeEmailIndex === index
                                    ? "2px solid #7e57c2"
                                    : "2px solid transparent",
                                flexShrink: 0,
                                zIndex: 1,
                              }}
                            >
                              BIZ Email {index + 1}
                            </Box>
                          ))}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexShrink: 0,
                            pl: 2,
                          }}
                        >
                          <Button
                            onClick={addEmailField}
                            variant="outlined"
                            aria-label="Add Email"
                            style={{
                              marginLeft: "10px",
                              minWidth: "30px",
                              height: "30px",
                            }}
                          >
                            <PlusOutlined />
                          </Button>

                          <IconButton
                            onClick={() => deleteEmailField(activeEmailIndex)}
                            aria-label="Delete Email"
                            size="medium"
                            color="error"
                            sx={{ ml: 1 }}
                          >
                            <DeleteFilled />
                          </IconButton>
                        </Box>
                      </Box>
                      {/* Email Input */}
                      <TextField
                        variant="filled"
                        fullWidth
                        id={`entity-email-${activeEmailIndex}`}
                        placeholder="BIZ Email"
                        value={formik.values.email[activeEmailIndex] || ""}
                        onChange={(e) =>
                          updateEmailField(activeEmailIndex, e.target.value)
                        }
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={6}>
                    <Stack spacing={1.25}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            flexGrow: 1,
                            overflowX: "auto",
                            overflowY: "hidden",
                            display: "flex",
                            whiteSpace: "nowrap",
                            "&::-webkit-scrollbar": { height: 6 },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#ccc",
                              borderRadius: "4px",
                            },
                          }}
                        >
                          {(formik.values.phone.length > 0
                            ? formik.values.phone
                            : [""]
                          ).map((_, index: number) => (
                            <Box
                              key={index}
                              onClick={() => setActivePhoneIndex(index)}
                              sx={{
                                cursor: "pointer",
                                px: 1.5,
                                py: 0.5,
                                fontSize: "0.875rem",
                                flexShrink: 0,
                                borderBottom:
                                  activePhoneIndex === index
                                    ? "2px solid #7e57c2"
                                    : "2px solid transparent",
                              }}
                            >
                              BIZ Phone {index + 1}
                            </Box>
                          ))}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexShrink: 0,
                            pl: 2,
                          }}
                        >
                          <Button
                            onClick={addPhoneField}
                            variant="outlined"
                            aria-label="Add Phone"
                            style={{
                              marginLeft: "10px",
                              minWidth: "30px",
                              height: "30px",
                            }}
                          >
                            <PlusOutlined />
                          </Button>

                          <IconButton
                            onClick={() => deletePhoneField(activePhoneIndex)}
                            aria-label="Delete Phone"
                            size="medium"
                            color="error"
                            sx={{ ml: 1 }}
                          >
                            <DeleteFilled />
                          </IconButton>
                        </Box>
                      </Box>
                      {/* Phone Input */}
                      <PatternFormat
                        variant="filled"
                        id={`entity-phone-${activePhoneIndex}`}
                        format="+1-###-###-####"
                        mask="_"
                        placeholder="BIZ Phone"
                        fullWidth
                        customInput={TextField}
                        value={formik.values.phone[activePhoneIndex] || ""}
                        onValueChange={(values) =>
                          updatePhoneField(
                            activePhoneIndex,
                            values.formattedValue,
                          )
                        }
                        error={Boolean(touched.phone && errors.phone)}
                        helperText={touched.phone && errors.phone}
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={6}>
                    <Stack spacing={1.25}>
                      <InputLabel htmlFor="entity-www">BIZ Web</InputLabel>
                      <TextField
                        variant="filled"
                        fullWidth
                        id="entity-www"
                        placeholder="BIZ Web"
                        {...getFieldProps("www")}
                        error={Boolean(touched.www && errors.www)}
                        helperText={touched.www && errors.www}
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={6}>
                    <Stack spacing={1.25}>
                      <InputLabel htmlFor="entity-taxid">Tax ID</InputLabel>
                      <PatternFormat
                        variant="filled"
                        format="###-##-####"
                        mask="_"
                        customInput={TextField}
                        placeholder="Tax Id"
                        fullWidth
                        id="entity-taxid"
                        {...getFieldProps("taxId")}
                        error={Boolean(touched.taxId && errors.taxId)}
                        helperText={touched.taxId && errors.taxId}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack spacing={1.25}>
                      <InputLabel htmlFor="linkToFiles" sx={{ mb: 0.5 }}>
                        Link to Files
                      </InputLabel>
                      <TextField
                        variant="filled"
                        fullWidth
                        id="linkToFiles"
                        placeholder="Link to Files"
                        {...getFieldProps("linkToFiles")}
                        error={Boolean(
                          touched.linkToFiles && errors.linkToFiles,
                        )}
                        helperText={touched.linkToFiles && errors.linkToFiles}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack spacing={1.25}>
                      <CompanyTypeAutocomplete
                        companyTypes={
                          companyTypes || [
                            CompanyType.Broker,
                            CompanyType.Merchant,
                            CompanyType.Legal,
                          ]
                        }
                        selectedCompanyTypes={values.type || []}
                        setSelectedCompanyTypes={(types) =>
                          formik.setFieldValue("type", types)
                        }
                        isInCompanyForm={true}
                        disabled={!!companyTypes}
                        error={Boolean(touched.type && errors.type)}
                        helperText={touched.type && errors.type}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
              <Box
                sx={{
                  display: activeSubTab === 1 ? "block" : "none",
                  p: 2.5,
                  minHeight: 480
                }}
              >
                <CompanyOwnersTab ref={ownersTabRef} formik={formik} />
              </Box>
            </DialogContent>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
      {/* add company dialog */}
      <Dialog maxWidth="sm" fullWidth onClose={handleCloseAvatarAdjustment} open={adjustAvatar} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        {adjustAvatar && (
          <ProfileAvatarEditor
            sourceImage={logo!}
            onSave={(image) => {
              setLogo(image.toDataURL());
              image.toBlob((blob: Blob) => {
                setMediaFile(blob as File);
              });
              handleCloseAvatarAdjustment();
            }}
            onClose={handleCloseAvatarAdjustment}
            onCancel={() => {
              setLogo(entity?.logo || undefined);
              handleCloseAvatarAdjustment();
            }}
          />
        )}
      </Dialog>
    </>
  );
});

export default CompanyInfoTab;